import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { formatPrice } from "../lib/api";
import { ProductVisual } from "./ProductVisual";
import { WishlistButton } from "./WishlistButton";
import { QuickView } from "./QuickView";

const QUICK_VIEW_ICON_SIZE = 13;

const SWATCHES = {
  "Dark Chocolate": "#3B2416",
  Black: "#141110",
  Ivory: "#F4EBE1",
  Beige: "#D9C7B2",
  Bronze: "#B87333",
  Cognac: "#9A5B2D",
  Espresso: "#2B1A10",
};

export const ProductCard = ({ product }) => {
  const [quickOpen, setQuickOpen] = useState(false);

  return (
    <div className="group relative" data-testid={`product-card-${product.slug}`}>
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-[3/4] overflow-hidden bg-capra-surface">
          <div className="h-full w-full transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]">
            <ProductVisual product={product} />
          </div>
          {product.is_new && (
            <span className="absolute top-3 left-3 z-10 px-2.5 py-1 bg-capra-night/80 backdrop-blur border border-capra-bronze/40 text-[9px] uppercase tracking-[0.25em] text-capra-bronze">
              New
            </span>
          )}
          {product.stock <= 0 && (
            <span
              data-testid={`sold-out-badge-${product.slug}`}
              className="absolute top-3 right-3 z-10 px-3 py-1.5 bg-capra-night/85 backdrop-blur border border-capra-bronze/40 text-[9px] uppercase tracking-[0.3em] text-capra-ivory/90"
            >
              Sold Out
            </span>
          )}
          <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 p-3 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
            <button
              data-testid={`quick-view-${product.slug}`}
              onClick={(e) => {
                e.preventDefault();
                setQuickOpen(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-capra-night/85 backdrop-blur border border-capra-bronze/40 text-[10px] uppercase tracking-[0.2em] text-capra-ivory hover:border-capra-bronze hover:text-capra-bronze transition-colors"
            >
              <Eye size={QUICK_VIEW_ICON_SIZE} strokeWidth={1.5} /> Quick View
            </button>
            <span className="bg-capra-night/85 backdrop-blur border border-capra-bronze/40">
              <WishlistButton product={product} />
            </span>
          </div>
        </div>

        <div className="pt-4 pb-2">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display text-base lg:text-lg text-capra-ivory leading-snug group-hover:text-capra-bronze transition-colors">
              {product.name}
            </h3>
            <p className="text-sm text-capra-gold whitespace-nowrap pt-0.5" data-testid={`product-price-${product.slug}`}>
              {formatPrice(product.price)}
            </p>
          </div>
          {product.colors?.length > 0 && (
            <div className="flex gap-1.5 mt-2.5">
              {product.colors.slice(0, 4).map((c) => (
                <span
                  key={c}
                  title={c}
                  className="h-3 w-3 rounded-full border border-white/20"
                  style={{ backgroundColor: SWATCHES[c] || "#5A4030" }}
                />
              ))}
            </div>
          )}
        </div>
      </Link>
      <QuickView product={product} open={quickOpen} onOpenChange={setQuickOpen} />
    </div>
  );
};
